import { keepPreviousData } from '@tanstack/react-query';
import { TableState } from '@optimus-web/core';
import {
    BoolFilterType,
    EmployeeLoadTypeEnum,
    EnumFilterType,
    StringFilterType,
    useRepositoryQuery,
    UserProfileFilters,
    UserProfileResult,
} from '@mews-commander/data-access';
import { FilterValues, UserSource, WithFilterValues } from '../components/UsersList/filterSchema';
import { useUserContext } from '../context';
import { UserType } from '../types';

export const useUserProfilesData = ({
    tableState,
    filterValues,
}: {
    tableState: TableState<UserProfileResult, UserProfileFilters>;
} & WithFilterValues) => {
    const { sort, pagination, globalFilters } = tableState;
    const { userType } = useUserContext();

    const employeeLoadType =
        userType === UserType.Portfolio
            ? EmployeeLoadTypeEnum.PortfolioEmployees
            : EmployeeLoadTypeEnum.PropertyEmployees;

    return useRepositoryQuery(
        'userProfiles',
        'getAll',
        {
            employeeLoadType,
            filters: parseFilterValues(filterValues),
            sort,
            pagination,
            ...(globalFilters.search && {
                globalSearchFilter: { value: globalFilters.search, filterType: StringFilterType.Contains },
            }),
        },
        {
            placeholderData: keepPreviousData,
        }
    );
};

const parseFilterValues = ({
    name,
    superiorName,
    roles,
    reset2fa,
    status,
    source,
    isAdmin,
}: FilterValues): UserProfileFilters => ({
    ...(name && { name: [{ value: name, filterType: StringFilterType.Contains }] }),
    ...(superiorName && { superiorName: [{ value: superiorName, filterType: StringFilterType.Contains }] }),
    ...(roles && { roles: [{ value: roles, filterType: StringFilterType.Contains }] }),
    ...(isAdmin && { isAdmin: [{ value: isAdmin, filterType: BoolFilterType.Equals }] }),
    ...(reset2fa && { canResetMfa: [{ value: reset2fa, filterType: BoolFilterType.Equals }] }),
    ...(status?.length && { status: [{ value: status, filterType: EnumFilterType.In }] }),
    isScimProvisioned: getSourceValue(source),
});

const getSourceValue = (source?: string | null) => {
    if (source === UserSource.Idp) return [{ value: true, filterType: BoolFilterType.Equals }];
    if (source === UserSource.Mews) return [{ value: false, filterType: BoolFilterType.Equals }];

    return null;
};

export const getActiveFilterCount = (filterValues: FilterValues) => {
    const filterValuesParsed = parseFilterValues(filterValues);
    return Object.values(filterValuesParsed).filter((value) => value !== null).length;
};
